import React, {useState} from "react";
import L from "leaflet";
// import {useNavigate} from "react-router-dom";
import PillMultiSelect from "./PillMultiSelect";
import {useTranslation} from 'react-i18next';
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    ZoomControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import "../../../css/spaces-map.css";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import Card from "@mui/material/Card";
import {CardActionArea, CardActions} from "@mui/material";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import CategoryFilter from "./space-filtering";

const media_path = window.location.origin;
const DefaultIcon = media_path + "/img/map_icons/default.svg";
const UnconfirmedIcon = media_path + "/img/map_icons/unconfirmed.svg";
const CARTO_KEY = "cb1_42yd_1_0261da4866620de6ae26db68"; 

// import Place from "../../types/places.type";

let markerclusterOptions = {
    disableClusteringAtZoom: 12,
    // maxClusterRadius: 100,
    spiderfyOnMaxZoom: false,
    animateAddingMarkers: true
};

function SpaceMap({generatePins, imagesPath}) {
    // const navigate = useNavigate();
    const {t} = useTranslation();

    let DefaultIcon = L.icon({
        iconUrl: icon,
        shadowUrl: iconShadow,
    });

    // Square meters (fixed buckets)
    const squareMetersOptions = [
        {value: '100', label: '100'},
        {value: '101_200', label: '101-200'},
        {value: '201_300', label: '201-300'},
        {value: '301_500', label: '301-500'},
        {value: '501_1000', label: '501-1000'},
        {value: '1000+', label: '1000+'}
    ];

    // Walls condition
    const wallsConditionOptions = [
        {value: 'intact', label: 'Βιώσιμη'},
        {value: 'partial', label: 'Μερεμέτια'},
        {value: 'collapsed', label: 'Γκρεμισμένο'},
    ];

    // Floors options (1–4, 5+). Adjust if you want more buckets.
    const floorsOptions = [
        {value: '1', label: '1'},
        {value: '2', label: '2'},
        {value: '3', label: '3'},
        {value: '4', label: '4'},
        {value: '5+', label: '5+'},
    ];

    // New filter states
    const [selectedSqm, setSelectedSqm] = useState([]);
    const [selectedWalls, setSelectedWalls] = useState([]);
    const [selectedFloors, setSelectedFloors] = useState([]);


    L.Marker.prototype.options.icon = DefaultIcon;
    const initialShowFilters = window.innerWidth >= 768;
    const [showFilters, setShowFilters] = useState(initialShowFilters); // State variable for showing/hiding filters
    const [tileServerIndex, setTileServerIndex] = useState(0); // Start with the primary server


    const tileServers = [
        {
            url: `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`,
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
        },
        {
            url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        },
    ];


    const handleTileError = () => {
        setTileServerIndex((currentIndex) =>
            currentIndex + 1 < tileServers.length ? currentIndex + 1 : 0 // Switch to next server or loop back
        );
    };


    // HERE WE LIMIT THE SCROLLING ON THE MAP
    // var southWest = L.latLng(40.453668, 22.972472),
    //     northEast = L.latLng(40.776838, 22.926328),
    //     bounds = L.latLngBounds(southWest, northEast);

    const greeceBounds = L.latLngBounds(
        L.latLng(34.5, 19.37), // Adjusted southwest corner to include Crete and Rhodes
        L.latLng(41.75, 26.35)  // Northeast corner remains the same
    );

    const METAXOYRGIO_POSITION = [37.98187799999999, 23.7183175];

    const [selectedBuildingTypes, setSelectedBuildingTypes] = useState([]);

    const normalizeFloors = (v) => {
        const n = parseInt(String(v||"").trim(), 10);
        if (Number.isNaN(n)) return "";        // unknown
        return n >= 5 ? "5+" : String(n);
    };
    const pinsWithBuildingType = generatePins.map((pin) => {
        const firstBuildingType = Array.isArray(pin.building_type) ? pin.building_type[0] : pin.category;
        return {
            ...pin,
            building_type: firstBuildingType,
            _sqm: (pin.squareMeters || "").trim(),                // '301_500', '1000+', etc.
            _wallsCond: (pin.walls?.condition || "").trim(),      // 'intact' | 'partial' | 'collapsed' | ''
            _floorsBucket: normalizeFloors(pin.floors),           // '1'...'4'|'5+'|''
        };
    });

    // const pinsWithBuildingType = generatePins.map((pin) => {
    //     const firstBuildingType = Array.isArray(pin.building_type) ? pin.building_type[0] : pin.category;
    //     return {
    //         ...pin,
    //         building_type: firstBuildingType
    //     };
    // });

    const availableSqm = Array.from(new Set(
        pinsWithBuildingType.map(p => p._sqm).filter(Boolean)
    ));

    const availableWalls = Array.from(new Set(
        pinsWithBuildingType.map(p => p._wallsCond).filter(Boolean)
    ));

    const availableFloors = Array.from(new Set(
        pinsWithBuildingType.map(p => p._floorsBucket).filter(Boolean)
    ));

    // Compute available categories from the spaces data (pins)
    const availableCategories = pinsWithBuildingType.reduce((acc, pin) => {
        if (pin.building_type && !acc.includes(pin.building_type)) {
            acc.push(pin.building_type);
        }
        return acc;
    }, []);

    const filteredMarkers = pinsWithBuildingType.filter((p) => {
        // building type
        const buildingTypePass =
            !selectedBuildingTypes.length ||
            selectedBuildingTypes.includes("no_category") ||
            selectedBuildingTypes.includes(p.building_type);

        // square meters
        const sqmPass =
            !selectedSqm.length || (p._sqm && selectedSqm.includes(p._sqm));

        // walls condition
        const wallsPass =
            !selectedWalls.length || (p._wallsCond && selectedWalls.includes(p._wallsCond));

        // floors bucket
        const floorsPass =
            !selectedFloors.length || (p._floorsBucket && selectedFloors.includes(p._floorsBucket));

        return buildingTypePass && sqmPass && wallsPass && floorsPass;
    });


    // const filteredMarkers = pinsWithBuildingType.filter((pinData) => {
    //     if (!selectedBuildingTypes.length || selectedBuildingTypes.includes("no_category")) {
    //         return true;
    //     }
    //     // Check if any of the pin's categories are included in the selected categories
    //     return selectedBuildingTypes.includes(pinData.building_type);
    // });


    return (
        <div>

            {/*<div className="map-go-back-button-to-select">*/}
            {/*    <div*/}
            {/*        className="select-region-back-button"*/}
            {/*        onClick={() => navigate('/map-select')}*/}
            {/*        style={{marginBottom: "10px"}}*/}
            {/*    >*/}
            {/*        &larr; {t("Select a region")}*/}
            {/*    </div>*/}
            {/*</div>*/}

            <div className="space-map-section">
                <MapContainer
                    center={METAXOYRGIO_POSITION}
                    zoom={17}
                    maxZoom={18}
                    minZoom={8}
                    scrollWheelZoom={true}
                    zoomControl={false}
                    maxBounds={greeceBounds} // Restrict panning to Greece's bounds
                    maxBoundsViscosity={0.1} // Makes the bounds fully solid, preventing the user from dragging outside
                >
                    <ZoomControl position="topleft"/>
                    <TileLayer
			key={tileServerIndex}
                        attribution={tileServers[tileServerIndex].attribution}
                        url={tileServers[tileServerIndex].url}
                        eventHandlers={{
                            tileerror: handleTileError,
                        }}
                    />
                    <MarkerClusterGroup {...markerclusterOptions}>
                        {filteredMarkers.map((pinData) => {
                            // console.log(pinData);
                            return createMarker(pinData, t, imagesPath);
                        })}
                    </MarkerClusterGroup>
                </MapContainer>
            </div>
            <div className="space-map-side-section">

                <div className="space-filters-section" data-showfilters={showFilters}>
                    <div className="space-filters-title" onClick={() => setShowFilters(!showFilters)}
                         style={{marginBottom: '10px'}}>
                        {showFilters ? t('Filters') : t('Filters')}
                    </div>
                    {showFilters && (
                        <div className="space-filters">
                            <div className="space-filters-block">
                                <CategoryFilter
                                    value={selectedBuildingTypes}
                                    availableCategories={availableCategories}
                                    onChange={(categoryValue) => setSelectedBuildingTypes(categoryValue)}
                                    t={t}
                                />
                            </div>

                            {/* Square meters */}
                            <div className="space-filters-block">
                                <PillMultiSelect
                                    label={t("Square meters")}
                                    options={squareMetersOptions}
                                    value={selectedSqm}
                                    onChange={setSelectedSqm}
                                    available={availableSqm}
                                    t={t}
                                />
                            </div>

                            {/* Walls condition */}
                            <div className="space-filters-block">
                                <PillMultiSelect
                                    label={t("Walls condition")}
                                    options={wallsConditionOptions}
                                    value={selectedWalls}
                                    onChange={setSelectedWalls}
                                    available={availableWalls}
                                    t={t}
                                />
                            </div>

                            {/* Floors */}
                            <div className="space-filters-block">
                                <PillMultiSelect
                                    label={t("Floors")}
                                    options={floorsOptions}
                                    value={selectedFloors}
                                    onChange={setSelectedFloors}
                                    available={availableFloors}
                                    t={t}
                                />
                            </div>
                        </div>
                        // <div className="space-filters">
                        //     <div className="space-filters-block">
                        //         <CategoryFilter
                        //             value={selectedBuildingTypes}
                        //             availableCategories={availableCategories}
                        //             onChange={(categoryValue) => setSelectedBuildingTypes(categoryValue)}
                        //             t={t}
                        //         />
                        //     </div>
                        // </div>
                    )}
                </div>
            </div>

        </div>

    );
}

function mapIcon(buildingType, confirmed) {
    if (!buildingType) {
        return DefaultIcon;
    } else {
        if (confirmed === false) {
            return UnconfirmedIcon;
        } else {
            return media_path + "/img/map_icons/" + buildingType + ".svg";
        }
    }
}

function createMarker(pinData, t, imagesPath) {

    // console.log(pinData);

    if (!pinData.lat || !pinData.lng || !pinData.name) {
        console.log(pinData);
    }

    const markerIcon = L.icon({
        iconUrl: mapIcon(pinData.building_type, pinData.confirmed),
        iconAnchor: [12.5, 21], // Anchor point is at the bottom middle of the icon
        popupAnchor: [12.5, -21], // Adjusts the popup to open above the marker
    });
    // const trList = (v) =>
    //     Array.isArray(v) && v.length
    //         ? v.map((x) => t(x)).join(", ")
    //         : t("Unknown");
    const trField = (v) => {
        if (typeof v === 'string' && v.trim() !== '') return t(v);
        if (v !== undefined && v !== null && v !== '') return String(v);
        return t('Unknown');
    };
    return (
        <Marker
            position={[pinData.lat, pinData.lng]}
            key={pinData.name}
            icon={markerIcon}
        >
            {/*{console.log(pinData)}*/}
            <Popup maxWidth={300}>
                <Card className="place-block-popup" data-pinid={pinData.name}>
                    <CardActionArea className="place-block-popup-area">
                        <a href={`/space-view/${pinData._id}`} target="_blank" rel="noopener noreferrer">

                            <CardMedia
                                className="place-block-popup-image"
                                component="img"
                                image={
                                    pinData.images && pinData.images.length > 0
                                        ? `${imagesPath || ''}${pinData.images[0].path}`
                                        : imagesPath + 'item-no-image.png'
                                }
                                alt={pinData.name}
                            />
                        </a>
                        <CardContent className="place-block-data">
                            <Typography gutterBottom variant="h6" component="div" className="block-place-name">
                                {pinData.name}
                            </Typography>
                            <div className="space-pin-header">
                                <div
                                    className="space-pin-header-text text-middle">{t("Building Type")}: {trField(pinData.building_type)}</div>
                            </div>
                            <div className={"block-place-description-block"}>
                                <div className={"block-place-description-title"}>
                                    Περιγραφή:
                                </div>
                                <Typography variant="body2" color="text.secondary" className="block-place-description">
                                    {pinData.description}
                                </Typography>
                            </div>
                            <div className={"block-place-address-block"}>
                                <div className={"block-place-address-title"}>
                                    Διεύθυνση:
                                </div>
                                <Typography variant="body2" color="text.secondary" className="block-place-address">
                                    {pinData.address}
                                </Typography>
                            </div>
                        </CardContent>
                    </CardActionArea>
                    <CardActions>
                        <div className="place-point-more-button">
                            <a href={`/space-view/${pinData._id}`} target="_blank" rel="noopener noreferrer">
                                {t("Περισσότερα")}
                            </a>
                        </div>
                    </CardActions>
                </Card>
            </Popup>
        </Marker>
    );
}

export default SpaceMap;
